/**
 * FamaPOS - Dashboard Page
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ShoppingCart,
  TrendingUp,
  Package,
  AlertTriangle,
  Clock,
  Euro,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  ChevronRight,
  Loader2,
  BarChart3,
  Boxes,
  ExternalLink,
} from 'lucide-react';
import api, {
  reportsAPI,
  batchesAPI,
  salesAPI,
  invoicesAPI,
  expensesAPI,
} from '../services/api';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { toast } from 'sonner';
import { useGlobalView } from '../context/GlobalViewContext';

const NOTIF_BACKEND_URL =
  process.env.REACT_APP_GEST_NOTIF_BACKEND_URL ||
  process.env.REACT_APP_BACKEND_URL;

const NOTIF_API = `${NOTIF_BACKEND_URL}/api`;

const AGENDA_START_HOUR = 8;
const AGENDA_END_HOUR = 20;
const AGENDA_TOTAL_MINUTES = (AGENDA_END_HOUR - AGENDA_START_HOUR) * 60;
const AGENDA_TIMELINE_HEIGHT = 960;

const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'emerald' }) => {
  const colorClasses = {
    emerald: 'bg-emerald-500/20 text-emerald-400',
    blue: 'bg-blue-500/20 text-blue-400',
    amber: 'bg-amber-500/20 text-amber-400',
    rose: 'bg-rose-500/20 text-rose-400',
    purple: 'bg-purple-500/20 text-purple-400',
  };

  return (
    <Card className="stat-card" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-400 mb-1">{title}</p>
          <p className="text-3xl font-bold text-white font-['Manrope']">{value}</p>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>

      {trend !== undefined && trend !== null && (
        <div className="mt-4 flex items-center gap-2">
          {trend >= 0 ? (
            <ArrowUpRight className="w-4 h-4 text-emerald-400" />
          ) : (
            <ArrowDownRight className="w-4 h-4 text-rose-400" />
          )}
          <span className={trend >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
            {Math.abs(trend).toFixed(1)}%
          </span>
          <span className="text-slate-500 text-sm">{trendValue}</span>
        </div>
      )}
    </Card>
  );
};

const BillingOverview = ({ stats }) => {
  const today = stats.todaySales;
  const yesterday = stats.yesterdaySales;
  const difference = today - yesterday;
  const percent = yesterday > 0 ? (difference / yesterday) * 100 : (today > 0 ? 100 : 0);
  const positive = difference >= 0;
  return (
    <Card className="card-dark p-0 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-700/70 flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-emerald-400" />
        <h2 className="text-lg font-semibold text-white">Resumo de Faturação</h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3">
        <section className="p-5 lg:border-r border-slate-700/70">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm text-slate-400">Hoje</p>
              <p className="mt-1 text-3xl font-bold text-white font-['Manrope']">€{today.toFixed(2)}</p>
              <p className="mt-1 text-sm text-slate-500">{stats.todayTransactions} transações</p>
            </div>
            <div className="rounded-xl bg-emerald-500/15 p-3 text-emerald-400"><ShoppingCart className="w-6 h-6" /></div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-3 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-400">Ervanária</p>
              <p className="mt-1 text-xl font-bold text-white">€{stats.todayRetailSales.toFixed(2)}</p>
              <p className="text-xs text-slate-500">{stats.todayRetailItems} {stats.todayRetailItems === 1 ? 'produto' : 'produtos'}</p>
            </div>
            <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 px-3 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-400">Clínica</p>
              <p className="mt-1 text-xl font-bold text-white">€{stats.todayClinicSales.toFixed(2)}</p>
              <p className="text-xs text-slate-500">{stats.todayClinicItems} {stats.todayClinicItems === 1 ? 'consulta' : 'consultas'}</p>
            </div>
          </div>
        </section>
        <section className="p-5 lg:border-r border-slate-700/70 flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-400">Este mês</p>
              <p className="mt-1 text-3xl font-bold text-white">€{stats.salesThisMonth.toFixed(2)}</p>
              <p className="mt-1 text-sm text-slate-500">Anterior: €{stats.salesPrevMonth.toFixed(2)}</p>
            </div>
            <div className="rounded-xl bg-blue-500/15 p-3 text-blue-400"><Calendar className="w-6 h-6" /></div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-3 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-400">Ervanária</p>
              <p className="mt-1 text-xl font-bold text-white">€{stats.monthRetailSales.toFixed(2)}</p>
              <p className="text-xs text-slate-500">{stats.monthRetailItems} {stats.monthRetailItems === 1 ? 'produto' : 'produtos'}</p>
            </div>
            <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 px-3 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-400">Clínica</p>
              <p className="mt-1 text-xl font-bold text-white">€{stats.monthClinicSales.toFixed(2)}</p>
              <p className="text-xs text-slate-500">{stats.monthClinicItems} {stats.monthClinicItems === 1 ? 'consulta' : 'consultas'}</p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <span className={`rounded-full px-2.5 py-1 text-sm font-semibold ${stats.monthlyTrend >= 0 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
              {stats.monthlyTrend >= 0 ? '+' : '-'}{Math.abs(stats.monthlyTrend).toFixed(1)}%
            </span>
            <span className="text-sm text-slate-500">vs. mês anterior</span>
          </div>
        </section>
        <section className="p-5 flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-400">Hoje vs. ontem</p>
              <p className={`mt-1 text-3xl font-bold ${positive ? 'text-emerald-400' : 'text-rose-400'}`}>
                {positive ? '+' : '-'}€{Math.abs(difference).toFixed(2)}
              </p>
              <p className="mt-1 text-sm text-slate-500">Ontem: €{yesterday.toFixed(2)}</p>
            </div>
            <div className={`rounded-xl p-3 ${positive ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
              {positive ? <ArrowUpRight className="w-6 h-6" /> : <ArrowDownRight className="w-6 h-6" />}
            </div>
          </div>
          <div className="mt-5">
            <span className={`rounded-full px-2.5 py-1 text-sm font-semibold ${positive ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
              {positive ? '+' : '-'}{Math.abs(percent).toFixed(1)}%
            </span>
          </div>
        </section>
      </div>
    </Card>
  );
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const ymd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const monthLabel = (date) =>
  date.toLocaleDateString('pt-PT', { month: 'short' }).replace('.', '');

const ptDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const safeArray = (value) => (Array.isArray(value) ? value : []);

const normalizeCalendarName = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const isPreferredDrMarioCalendar = (calendar) => {
  const name = normalizeCalendarName(calendar?.name);
  return (
    name.includes('dr') &&
    name.includes('mario') &&
    name.includes('tinoco')
  );
};

const toClockMinutes = (date) => (date.getHours() * 60) + date.getMinutes();

const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

const formatHourLabel = (hour) => `${String(hour).padStart(2, '0')}:00`;

const openGoogleCalendarDay = (dateStr) => {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const url = `https://calendar.google.com/calendar/r/day/${year}/${month}/${day}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const getStoredAuthToken = () => (
  localStorage.getItem('ervapos_token') ||
  localStorage.getItem('token') ||
  localStorage.getItem('access_token') ||
  localStorage.getItem('authToken') ||
  ''
);

const fetchJson = async (url, options = {}) => {
  const token = getStoredAuthToken();
  const headers = token
    ? { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` }
    : {};

  const res = await fetch(url, {
    ...options,
    headers: { ...headers, ...(options.headers || {}) },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
};

const loadSyncedDashboardAgenda = async () => {
  const calendars = safeArray(await fetchJson(`${NOTIF_API}/calendars`));
  // O Dashboard é apenas leitura. Sincronizar milhares de eventos aqui bloqueava
  // o mesmo backend usado pelo POS. A sincronização fica a cargo do módulo Agenda.
  const [futureAppointments, pastAppointments] = await Promise.all([
    fetchJson(`${NOTIF_API}/appointments/future?limit=100`),
    fetchJson(`${NOTIF_API}/appointments/past?limit=100`),
  ]);
  const appointmentsById = new Map();
  [...safeArray(futureAppointments), ...safeArray(pastAppointments)].forEach((appointment) => {
    const key = String(appointment?.id || appointment?.uid || `${appointment?.calendar_id}:${appointment?.appointment_date}`);
    appointmentsById.set(key, appointment);
  });
  return { appointments: Array.from(appointmentsById.values()), calendars };
};

export const DashboardPage = () => {
  const { isGlobalView } = useGlobalView();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    todaySales: 0,
    todayTransactions: 0,
    todayRetailSales: 0,
    todayClinicSales: 0,
    todayRetailItems: 0,
    todayClinicItems: 0,
    yesterdaySales: 0,
    expiringProducts: 0,
    suspendedSales: 0,
    activeCustomers: 0,
    totalProducts: 0,
    salesThisMonth: 0,
    salesPrevMonth: 0,
    monthRetailSales: 0,
    monthClinicSales: 0,
    monthRetailItems: 0,
    monthClinicItems: 0,
    monthlyTrend: 0,
    appointmentsToday: 0,
  });

  const [salesData, setSalesData] = useState([]);
  const [monthlySalesData, setMonthlySalesData] = useState([]);
  const [expiringBatches, setExpiringBatches] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [customerOrdersStats, setCustomerOrdersStats] = useState({
    pending: 0,
    arrived: 0,
    notified: 0,
    collected: 0,
  });

  const [monthlyFinanceStats, setMonthlyFinanceStats] = useState({
    purchasesTotal: 0,
    purchasesCount: 0,
    expensesTotal: 0,
    expensesCount: 0,
  });

  const [agendaLoading, setAgendaLoading] = useState(true);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [calendarOptions, setCalendarOptions] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState('');
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    loadDashboardData();
  }, [isGlobalView]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const getDaysUntilExpiry = (expiryDate) => {
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getExpiryBadge = (days) => {
    if (days < 0) {
      return <Badge className="bg-rose-600 hover:bg-rose-600 text-white border-0">Expirado</Badge>;
    }
    if (days <= 30) {
      return <Badge className="bg-amber-500 hover:bg-amber-500 text-slate-950 border-0">Crítico</Badge>;
    }
    if (days <= 90) {
      return <Badge className="bg-orange-600 hover:bg-orange-600 text-white border-0">Atenção</Badge>;
    }
    return <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white border-0">OK</Badge>;
  };

  const loadDashboardData = async () => {
    setLoading(true);
    setAgendaLoading(true);

    const today = new Date();

    const currentMonthStart = startOfMonth(today);
    const currentMonthEnd = endOfMonth(today);

    try {
      const [
        dashboardSummaryRes,
        expiringRes,
        suspendedRes,
      ] = await Promise.all([
        isGlobalView ? reportsAPI.getDashboardSummary() : Promise.resolve({ data: {} }),
        batchesAPI.getExpiring(180),
        salesAPI.getSuspended(),
      ]);

      const dashboardSummary = dashboardSummaryRes?.data || {};
      const expiringList = safeArray(expiringRes?.data);
      const suspendedList = safeArray(suspendedRes?.data);

      const groupedMap = {};
      expiringList.forEach((batch) => {
        const productId = batch.product_id || batch.id || batch.product_name;
        if (!productId) return;

        const quantity = Number(batch.quantity || 0);
        const expiryDate = batch.expiry_date || '';

        if (!groupedMap[productId]) {
          groupedMap[productId] = {
            product_id: batch.product_id || '',
            product_name: batch.product_name || 'Produto',
            earliest_expiry_date: expiryDate,
            total_quantity: quantity,
            draft_return_quantity: Number(batch.draft_return_quantity || 0),
            draft_return_documents: batch.draft_return_documents || [],
            batches_count: 1,
          };
        } else {
          groupedMap[productId].total_quantity += quantity;
          groupedMap[productId].draft_return_quantity += Number(batch.draft_return_quantity || 0);
          groupedMap[productId].draft_return_documents = [...new Set([...(groupedMap[productId].draft_return_documents || []), ...(batch.draft_return_documents || [])])];
          groupedMap[productId].batches_count += 1;

          if (
            expiryDate &&
            (
              !groupedMap[productId].earliest_expiry_date ||
              expiryDate < groupedMap[productId].earliest_expiry_date
            )
          ) {
            groupedMap[productId].earliest_expiry_date = expiryDate;
          }
        }
      });

      const groupedExpiringProducts = Object.values(groupedMap).sort(
        (a, b) => (a.earliest_expiry_date || '9999-12-31').localeCompare(b.earliest_expiry_date || '9999-12-31')
      );

      const last7Days = safeArray(dashboardSummary.last_7_days);
      const monthlyData = safeArray(dashboardSummary.monthly_sales);
      const topProductsList = safeArray(dashboardSummary.top_products);
      const salesThisMonth = Number(dashboardSummary.current_month_total || 0);
      const salesPrevMonth = Number(dashboardSummary.previous_month_total || 0);

      const monthlyTrend =
        salesPrevMonth > 0
          ? ((salesThisMonth - salesPrevMonth) / salesPrevMonth) * 100
          : (salesThisMonth > 0 ? 100 : 0);

      setStats((prev) => ({
        ...prev,
        todaySales: Number(dashboardSummary?.today?.total_sales || 0),
        todayTransactions: Number(dashboardSummary?.today?.transaction_count || 0),
        todayRetailSales: Number(dashboardSummary?.today?.retail_sales || 0),
        todayClinicSales: Number(dashboardSummary?.today?.clinic_sales || 0),
        todayRetailItems: Number(dashboardSummary?.today?.retail_items || 0),
        todayClinicItems: Number(dashboardSummary?.today?.clinic_items || 0),
        yesterdaySales: Number(dashboardSummary?.yesterday?.total_sales || 0),
        expiringProducts: groupedExpiringProducts.length,
        suspendedSales: suspendedList.length,
        salesThisMonth,
        salesPrevMonth,
        monthRetailSales: Number(dashboardSummary?.current_month?.retail_sales || 0),
        monthClinicSales: Number(dashboardSummary?.current_month?.clinic_sales || 0),
        monthRetailItems: Number(dashboardSummary?.current_month?.retail_items || 0),
        monthClinicItems: Number(dashboardSummary?.current_month?.clinic_items || 0),
        monthlyTrend,
      }));

      setSalesData(last7Days);
      setMonthlySalesData(monthlyData);
      setExpiringBatches(groupedExpiringProducts.slice(0, 5));
      setTopProducts(topProductsList);
    } catch (error) {
      console.error('Error loading dashboard core:', error);
      toast.error('Erro ao carregar dados principais do dashboard');
    } finally {
      setLoading(false);
    }

    api.get('/dashboard-counts')
      .then((countsRes) => {
        const counts = countsRes?.data || {};
        setStats((prev) => ({
          ...prev,
          activeCustomers: Number(counts.active_customers || 0),
          totalProducts: Number(counts.active_products || 0),
        }));
      })
      .catch((err) => {
        console.error('Error loading counts:', err);
      });

    if (isGlobalView) Promise.all([
      invoicesAPI.getAll({
        date_from: ymd(currentMonthStart),
        date_to: ymd(currentMonthEnd),
      }).catch(() => ({ data: [] })),
      expensesAPI.getAll({
        date_from: ymd(currentMonthStart),
        date_to: ymd(currentMonthEnd),
      }).catch(() => ({ data: [] })),
    ])
      .then(([invoicesRes, expensesRes]) => {
        const invoices = safeArray(invoicesRes?.data);
        const expenses = safeArray(expensesRes?.data);

        setMonthlyFinanceStats({
          purchasesTotal: invoices.reduce((sum, row) => sum + Number(row?.total || row?.total_amount || 0), 0),
          purchasesCount: invoices.length,
          expensesTotal: expenses.reduce((sum, row) => sum + Number(row?.amount || 0), 0),
          expensesCount: expenses.length,
        });
      })
      .catch((err) => {
        console.error('Error loading monthly purchases/expenses:', err);
        setMonthlyFinanceStats({
          purchasesTotal: 0,
          purchasesCount: 0,
          expensesTotal: 0,
          expensesCount: 0,
        });
      });

    Promise.all([
      api.get('/customer-orders/dashboard').catch(() => ({ data: {} })),
      loadSyncedDashboardAgenda().catch((error) => {
        console.error('Error syncing dashboard agenda:', error);
        return { appointments: [], calendars: [] };
      }),
    ])
      .then(([ordersDashboardRes, agendaData]) => {
        const appointmentsList = safeArray(agendaData?.appointments);
        const calendarsList = safeArray(agendaData?.calendars);

        const todayOnly = appointmentsList
          .filter((apt) => {
            const d = new Date(apt.appointment_date);
            return !Number.isNaN(d.getTime()) && sameDay(d, today);
          })
          .sort((a, b) => String(a.appointment_date || '').localeCompare(String(b.appointment_date || '')));

        const dash = ordersDashboardRes?.data || {};

        setTodayAppointments(todayOnly);
        setCalendarOptions(calendarsList);
        setCustomerOrdersStats({
          pending: Number(dash.pending || 0),
          arrived: Number(dash.arrived || 0),
          notified: Number(dash.notified || 0),
          collected: Number(dash.collected || 0),
        });

        setStats((prev) => ({
          ...prev,
          appointmentsToday: todayOnly.length,
        }));
      })
      .catch((agendaError) => {
        console.error('Error loading agenda/orders:', agendaError);
        setTodayAppointments([]);
        setCalendarOptions([]);
        setCustomerOrdersStats({
          pending: 0,
          arrived: 0,
          notified: 0,
          collected: 0,
        });
      })
      .finally(() => {
        setAgendaLoading(false);
      });
  };

  const derivedCalendarOptions = useMemo(() => {
    const map = new Map();

    safeArray(calendarOptions).forEach((calendar) => {
      const id = String(calendar?.id || '').trim();
      if (!id) return;
      map.set(id, {
        id,
        name: calendar?.name || id,
      });
    });

    safeArray(todayAppointments).forEach((apt) => {
      const id = String(apt?.calendar_id || '').trim();
      if (!id || map.has(id)) return;
      map.set(id, {
        id,
        name: apt?.calendar_name || apt?.calendar || id,
      });
    });

    return Array.from(map.values()).sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt'));
  }, [calendarOptions, todayAppointments]);

  const filteredTodayAppointments = useMemo(() => {
    return safeArray(todayAppointments).filter((apt) => {
      const calendarId = String(apt?.calendar_id || '').trim();
      return !selectedCalendar || calendarId === selectedCalendar;
    });
  }, [todayAppointments, selectedCalendar]);

  useEffect(() => {
    if (!derivedCalendarOptions.length) return;
    if (selectedCalendar && derivedCalendarOptions.some((c) => c.id === selectedCalendar)) return;

    const preferred =
      derivedCalendarOptions.find(isPreferredDrMarioCalendar) ||
      derivedCalendarOptions.find((c) => normalizeCalendarName(c?.name).includes('mario tinoco')) ||
      derivedCalendarOptions.find((c) => normalizeCalendarName(c?.name).includes('tinoco'));

    if (preferred?.id) {
      setSelectedCalendar(preferred.id);
      return;
    }

    if (derivedCalendarOptions[0]?.id) {
      setSelectedCalendar(derivedCalendarOptions[0].id);
    }
  }, [derivedCalendarOptions, selectedCalendar]);

  const agendaBlocks = useMemo(() => {
    const pxPerMinute = AGENDA_TIMELINE_HEIGHT / AGENDA_TOTAL_MINUTES;

    const baseBlocks = filteredTodayAppointments
      .map((apt, idx) => {
        const start = new Date(apt.appointment_date);
        const end = new Date(apt.appointment_end || apt.end_date || apt.end || '');

        if (Number.isNaN(start.getTime())) return null;

        const startMinutes = toClockMinutes(start);
        const startRelative = startMinutes - (AGENDA_START_HOUR * 60);

        const durationMinutes = Number(
          apt?.duration_minutes ||
          apt?.duration ||
          apt?.duration_min ||
          (
            !Number.isNaN(end.getTime())
              ? Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000))
              : 30
          )
        ) || 30;

        const endMinutes = startMinutes + durationMinutes;
        const top = clamp(startRelative * pxPerMinute, 0, AGENDA_TIMELINE_HEIGHT - 24);
        const height = clamp(Math.max((durationMinutes * pxPerMinute) - 6, 38), 38, 210);

        const doctorName = String(apt?.doctor || apt?.professional || '').trim();
        const specialty = String(apt?.specialty || '').trim();
        const patient = String(apt?.patient_name || 'Paciente').trim();

        return {
          key: apt.id || `${apt.appointment_date}-${idx}`,
          apt,
          startMinutes,
          endMinutes,
          top,
          height,
          timeLabel: start.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }),
          doctorName,
          specialty,
          patient,
          overlapColumn: 0,
          overlapColumns: 1,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes);

    const groups = [];
    let currentGroup = [];
    let currentGroupEnd = -1;

    baseBlocks.forEach((block) => {
      if (!currentGroup.length || block.startMinutes < currentGroupEnd) {
        currentGroup.push(block);
        currentGroupEnd = Math.max(currentGroupEnd, block.endMinutes);
      } else {
        groups.push(currentGroup);
        currentGroup = [block];
        currentGroupEnd = block.endMinutes;
      }
    });

    if (currentGroup.length) groups.push(currentGroup);

    groups.forEach((group) => {
      const columnEnds = [];

      group.forEach((block) => {
        let column = columnEnds.findIndex((end) => end <= block.startMinutes);

        if (column === -1) {
          column = columnEnds.length;
          columnEnds.push(block.endMinutes);
        } else {
          columnEnds[column] = block.endMinutes;
        }

        block.overlapColumn = column;
      });

      const totalColumns = Math.max(1, columnEnds.length);

      group.forEach((block) => {
        block.overlapColumns = totalColumns;
      });
    });

    return baseBlocks;
  }, [filteredTodayAppointments]);

  const appointmentsByProfessional = useMemo(() => {
    const map = {};

    // Este cartão deve contar todos os profissionais com consultas hoje,
    // independentemente da agenda selecionada no filtro visual da timeline.
    safeArray(todayAppointments).forEach((apt) => {
      const name = String(
        apt?.doctor ||
        apt?.professional ||
        apt?.professional_name ||
        apt?.provider_name ||
        apt?.calendar_name ||
        apt?.calendar ||
        'Sem profissional'
      ).trim() || 'Sem profissional';

      map[name] = (map[name] || 0) + 1;
    });

    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'pt'));
  }, [todayAppointments]);

  const nowLineTop = useMemo(() => {
    const now = new Date(nowTick);
    const nowMinutes = toClockMinutes(now);
    const startMinutes = AGENDA_START_HOUR * 60;
    const endMinutes = AGENDA_END_HOUR * 60;

    if (nowMinutes < startMinutes || nowMinutes > endMinutes) return null;

    const pxPerMinute = AGENDA_TIMELINE_HEIGHT / AGENDA_TOTAL_MINUTES;
    return (nowMinutes - startMinutes) * pxPerMinute;
  }, [nowTick]);

  const expiryStats = useMemo(() => ({
    expired: (expiringBatches || []).filter(p => {
      const expiryDate = p.earliest_expiry_date || p.expiry_date || '';
      return expiryDate && getDaysUntilExpiry(expiryDate) < 0;
    }).length,
    critical: (expiringBatches || []).filter(p => {
      const expiryDate = p.earliest_expiry_date || p.expiry_date || '';
      if (!expiryDate) return false;
      const d = getDaysUntilExpiry(expiryDate);
      return d >= 0 && d <= 30;
    }).length,
  }), [expiringBatches]);

  const handleOpenExpiryAlert = () => {
    navigate('/stock?tab=expiring');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Dashboard</h1>
          <p className="text-slate-400">Visão geral do negócio</p>
        </div>
        <Button
          onClick={() => navigate('/pos')}
          className="h-12 px-6 bg-emerald-500 hover:bg-emerald-600 text-white font-medium"
          data-testid="go-to-pos-btn"
        >
          <ShoppingCart className="w-5 h-5 mr-2" />
          Abrir POS
        </Button>
      </div>

      {isGlobalView && <BillingOverview stats={stats} />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div onClick={handleOpenExpiryAlert} className="cursor-pointer">
          <StatCard
            title="Produtos a Expirar"
            value={
              expiryStats.expired > 0
                ? `🚨 ${expiryStats.expired}`
                : expiryStats.critical > 0
                  ? `⚠️ ${expiryStats.critical}`
                  : '✔️'
            }
            subtitle={
              expiryStats.expired > 0
                ? 'Produtos expirados'
                : expiryStats.critical > 0
                  ? 'Críticos (≤30 dias)'
                  : 'Tudo OK'
            }
            icon={AlertTriangle}
            color={expiryStats.expired > 0 ? 'rose' : expiryStats.critical > 0 ? 'amber' : 'emerald'}
          />
        </div>

        <StatCard
          title="Vendas Suspensas"
          value={stats.suspendedSales}
          subtitle="A aguardar"
          icon={Clock}
          color="blue"
        />

        <StatCard
          title="Consultas Hoje"
          value={stats.appointmentsToday}
          subtitle="Todas as agendas"
          icon={Calendar}
          color="amber"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="card-dark p-6 xl:col-span-2">
          <CardHeader className="p-0 pb-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-400" />
                Agenda de Hoje
              </CardTitle>

              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={selectedCalendar}
                  onChange={(e) => setSelectedCalendar(e.target.value)}
                  className="h-10 rounded-lg border border-slate-700 bg-slate-900 px-3 text-sm text-white min-w-[320px]"
                >
                  {derivedCalendarOptions.map((calendar) => (
                    <option key={calendar.id} value={calendar.id}>{calendar.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <p className="text-sm text-slate-400 mt-2">
              Linha vermelha marca a hora atual. Clique num evento para abrir no Google Calendar.
            </p>
          </CardHeader>

          <CardContent className="p-0">
            {agendaLoading ? (
              <div className="flex items-center justify-center h-[1000px]">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : filteredTodayAppointments.length === 0 ? (
              <div className="h-[1000px] flex flex-col items-center justify-center text-center text-slate-500">
                <Calendar className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-lg font-semibold text-slate-300">Sem consultas hoje</p>
                <p className="text-sm text-slate-500">Não encontrei eventos para o filtro atual.</p>
              </div>
            ) : (
              <div className="flex gap-4">
                <div className="w-16 flex-shrink-0 relative" style={{ height: `${AGENDA_TIMELINE_HEIGHT}px` }}>
                  {Array.from({ length: AGENDA_END_HOUR - AGENDA_START_HOUR + 1 }).map((_, idx) => {
                    const hour = AGENDA_START_HOUR + idx;
                    const top = idx * (AGENDA_TIMELINE_HEIGHT / (AGENDA_END_HOUR - AGENDA_START_HOUR));
                    return (
                      <div
                        key={hour}
                        className="absolute left-0 right-0 -translate-y-1/2 text-xs text-slate-500"
                        style={{ top: `${top}px` }}
                      >
                        {hour < AGENDA_END_HOUR ? formatHourLabel(hour) : ''}
                      </div>
                    );
                  })}
                </div>

                <div
                  className="flex-1 relative rounded-xl overflow-hidden"
                  style={{
                    height: `${AGENDA_TIMELINE_HEIGHT}px`,
                    background: 'rgba(15,23,42,0.35)',
                    border: '1px solid rgba(148,163,184,0.18)',
                  }}
                >
                  {Array.from({ length: AGENDA_END_HOUR - AGENDA_START_HOUR + 1 }).map((_, idx) => {
                    const top = idx * (AGENDA_TIMELINE_HEIGHT / (AGENDA_END_HOUR - AGENDA_START_HOUR));
                    return (
                      <div
                        key={idx}
                        className="absolute left-0 right-0 border-t border-dashed border-slate-700/70"
                        style={{ top: `${top}px` }}
                      />
                    );
                  })}

                  {nowLineTop !== null && (
                    <div
                      className="absolute left-0 right-0 z-20"
                      style={{ top: `${nowLineTop}px` }}
                    >
                      <div className="relative">
                        <div className="absolute -left-2 -top-2 h-4 w-4 rounded-full bg-rose-500 border-2 border-white shadow" />
                        <div className="h-[2px] bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.6)]" />
                      </div>
                    </div>
                  )}

                  {agendaBlocks.map((block) => {
                    const eventUrl =
                      block.apt?.google_event_html_link ||
                      block.apt?.html_link ||
                      block.apt?.google_calendar_url ||
                      null;

                    return (
                      <button
                        key={block.key}
                        type="button"
                        onClick={() => {
                          if (eventUrl) {
                            window.open(eventUrl, '_blank', 'noopener,noreferrer');
                            return;
                          }
                          openGoogleCalendarDay(block.apt?.appointment_date);
                        }}
                        className="absolute rounded-xl border px-2.5 py-1.5 text-left transition-all hover:scale-[1.01] hover:shadow-lg overflow-hidden"
                        style={{
                          top: `${block.top}px`,
                          height: `${block.height}px`,
                          left: `calc(${(block.overlapColumn / block.overlapColumns) * 100}% + 6px)`,
                          width: `calc(${100 / block.overlapColumns}% - 12px)`,
                          background: 'linear-gradient(135deg, rgba(16,185,129,0.22), rgba(59,130,246,0.16))',
                          borderColor: 'rgba(74,222,128,0.34)',
                        }}
                        title={`${block.timeLabel} · ${block.patient}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[13px] leading-tight font-bold text-white truncate">
                              {block.timeLabel} · {block.patient}
                            </p>
                            <p className="text-[11px] leading-tight text-emerald-300 font-medium truncate mt-0.5">
                              {block.specialty || 'Consulta'}
                            </p>
                            <p className="text-[11px] leading-tight text-slate-300 truncate mt-0.5">
                              {block.doctorName || 'Prestador não definido'}
                            </p>
                          </div>

                          <ExternalLink className="w-3.5 h-3.5 text-slate-300 flex-shrink-0 mt-0.5" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 gap-6 content-start">
          <Card className="card-dark p-6">
            <CardHeader className="p-0 pb-3">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Boxes className="w-5 h-5 text-blue-400" />
                Enc. Clientes
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-slate-800/50 p-3">
                  <p className="text-xs text-slate-400">Pendentes</p>
                  <p className="text-2xl font-bold text-white">{customerOrdersStats.pending}</p>
                </div>
                <div className="rounded-xl bg-slate-800/50 p-3">
                  <p className="text-xs text-slate-400">Chegaram</p>
                  <p className="text-2xl font-bold text-amber-400">{customerOrdersStats.arrived}</p>
                </div>
                <div className="rounded-xl bg-slate-800/50 p-3">
                  <p className="text-xs text-slate-400">Notificadas</p>
                  <p className="text-2xl font-bold text-blue-400">{customerOrdersStats.notified}</p>
                </div>
                <div className="rounded-xl bg-slate-800/50 p-3">
                  <p className="text-xs text-slate-400">Levantadas</p>
                  <p className="text-2xl font-bold text-emerald-400">{customerOrdersStats.collected}</p>
                </div>
              </div>

              <Button
                onClick={() => navigate('/customer-orders')}
                variant="outline"
                className="w-full border-slate-700 text-white hover:bg-slate-800"
              >
                Abrir encomendas
              </Button>
            </CardContent>
          </Card>

          <Card className="card-dark p-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-slate-800/40 p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-400">Clientes Ativos</p>
                  <p className="text-xl font-bold text-white leading-tight">{stats.activeCustomers}</p>
                </div>
              </div>

              <div className="rounded-xl bg-slate-800/40 p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <Package className="w-5 h-5 text-purple-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-400">Total Produtos</p>
                  <p className="text-xl font-bold text-white leading-tight">{stats.totalProducts}</p>
                </div>
              </div>
            </div>
          </Card>

          <Card className="card-dark p-4">
            <CardHeader className="p-0 pb-3">
              <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
                <Calendar className="w-4 h-4 text-amber-400" />
                Consultas por Profissional
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {appointmentsByProfessional.length === 0 ? (
                <p className="text-sm text-slate-500">Sem consultas hoje</p>
              ) : (
                <div className="space-y-2">
                  {appointmentsByProfessional.slice(0, 5).map((row) => (
                    <div key={row.name} className="flex items-center justify-between gap-3 rounded-xl bg-slate-800/40 px-3 py-2">
                      <p className="text-xs text-slate-300 truncate">{row.name}</p>
                      <p className="text-sm font-bold text-white">{row.count}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className={`${!isGlobalView ? 'hidden ' : ''}card-dark p-4`}>
            <CardHeader className="p-0 pb-3">
              <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
                <Euro className="w-4 h-4 text-emerald-400" />
                Compras e Despesas do Mês
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-slate-800/40 p-3">
                  <p className="text-xs text-slate-400">Compras</p>
                  <p className="text-lg font-bold text-purple-300">€{monthlyFinanceStats.purchasesTotal.toFixed(2)}</p>
                  <p className="text-[11px] text-slate-500">{monthlyFinanceStats.purchasesCount} fatura(s)</p>
                </div>
                <div className="rounded-xl bg-slate-800/40 p-3">
                  <p className="text-xs text-slate-400">Despesas</p>
                  <p className="text-lg font-bold text-rose-300">€{monthlyFinanceStats.expensesTotal.toFixed(2)}</p>
                  <p className="text-[11px] text-slate-500">{monthlyFinanceStats.expensesCount} registo(s)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="card-dark p-6 xl:col-span-2">
          <CardHeader className="p-0 pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                Produtos a Expirar
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/stock?tab=expiring')}
                className="text-slate-400 hover:text-white"
              >
                Ver todos
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {expiringBatches.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum produto a expirar</p>
              </div>
            ) : (
              <div className="space-y-3">
                {expiringBatches.map((batch, index) => {
                  const expiryDate = batch.earliest_expiry_date || batch.expiry_date || '';
                  const totalQuantity = Number(batch.total_quantity ?? batch.quantity ?? 0);
                  const lotsCount = Number(batch.batches_count ?? 1);
                  const days = expiryDate ? getDaysUntilExpiry(expiryDate) : 9999;

                  return (
                    <div
                      key={batch.product_id || batch.product_name || index}
                      className={`flex items-center justify-between p-3 bg-slate-800/50 rounded-lg ${Number(batch.draft_return_quantity || 0)>0?'opacity-70 ring-1 ring-amber-500/20':''}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {batch.product_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          Validade mais próxima: {expiryDate || '—'} | Qtd total: {totalQuantity} | Lotes: {lotsCount}
                        </p>
                        {Number(batch.draft_return_quantity || 0)>0&&<p className="text-xs text-amber-300">{batch.draft_return_quantity} un. retidas para devolução ({(batch.draft_return_documents||[]).join(', ')})</p>}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-400">
                          {days === 9999 ? '—' : `${days} dias`}
                        </span>
                        {getExpiryBadge(days)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className={`${!isGlobalView ? 'hidden ' : ''}card-dark p-6`}>
          <CardHeader className="p-0 pb-4">
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Boxes className="w-5 h-5 text-purple-400" />
              Top Produtos
            </CardTitle>
          </CardHeader>

          <CardContent className="p-0">
            {topProducts.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Sem vendas para analisar</p>
              </div>
            ) : (
              <div className="space-y-4">
                {topProducts.map((product, idx) => {
                  const maxTotal = Math.max(...topProducts.map((p) => Number(p.total || 0)), 1);
                  const width = Math.max(8, (Number(product.total || 0) / maxTotal) * 100);

                  return (
                    <div
                      key={`${product.name}-${idx}`}
                      className="rounded-xl border border-slate-800 bg-slate-900/50 p-4"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0">
                          <p className="text-white font-semibold truncate">{product.name}</p>
                          <p className="text-slate-400 text-sm">{product.quantity} unidade(s)</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-emerald-400 font-bold">€{Number(product.total || 0).toFixed(2)}</p>
                        </div>
                      </div>

                      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                        <div
                          className="h-2 rounded-full"
                          style={{
                            width: `${width}%`,
                            background: 'linear-gradient(90deg, #a855f7, #10b981)',
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {isGlobalView && <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="card-dark p-6 xl:col-span-2">
          <CardHeader className="p-0 pb-4">
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              Vendas - Últimos 7 Dias
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={salesData}>
                  <defs>
                    <linearGradient id="colorVendas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      color: '#f8fafc'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="vendas"
                    stroke="#10b981"
                    fillOpacity={1}
                    fill="url(#colorVendas)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark p-6">
          <CardHeader className="p-0 pb-4">
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              Vendas Mensais
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlySalesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip
                    formatter={(value) => [`€${Number(value || 0).toFixed(2)}`, 'Vendas']}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.label || label}
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      color: '#f8fafc'
                    }}
                  />
                  <Bar dataKey="vendas" fill="#60a5fa" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>}

    </div>
  );
};

export default DashboardPage;
